import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import { FormattedMessage } from "react-intl";
import { PrimaryButton, SecondaryButton, DestructableButton } from "components/Buttons";
import { breakpoints, colors, fontSize, margins } from "style";

/**
 * Shows a Modal
 *
 * @params {Boolean}    show
 * @params {func}       onClose
 * @params {Element}    title
 * @params {Boolean}    show:
 * @params {Function}   onClose
 * @params {Function}   onSubmit
 * @params {Boolean}    showSave
 * @params {Boolean}    showSaveAsDestructable
 * @params {Boolean}    showCancel
 * @params {Boolean}    loading
 * @params {String}     size
 * @params {Boolean}    isDestructable
 * @params {Boolean}    canSubmit
 * @params {Element}    children
 * @params {String}     primaryButtonMessageId
 * @params {String}     secondaryButtonMessageId
 * @params {String}     footer
 * @params {Boolean}     disabled
 * @params {Node}       aside       Side panel beside the body and footer, scrolling on its own
 */
const ModalWindow = ({
  title,
  subTitle,
  info,
  show,
  onClose,
  onDestructive,
  onSubmit,
  showSave,
  showSaveAsDestructable,
  showCancel,
  loading,
  size = "sm",
  centered,
  isDestructable,
  canSubmit,
  children,
  primaryButtonMessageId,
  secondaryButtonMessageId,
  desctructableButtonMessageId,
  footer,
  disabled,
  clickOutsideToClose,
  aside,
  ...props
}) => {
  const content = (
    <>
      <Modal.Header closeButton={showCancel}>
        <Modal.Title>
          <div css={styles.header}>
            <div>{title}</div>
            <div css={styles.info}>{info}</div>
          </div>
          {subTitle && <div css={styles.subTitle}>{subTitle}</div>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>
        <div css={styles.footer_container}>
          {isDestructable && (
            <DestructableButton onClick={onDestructive} disabled={loading}>
              <FormattedMessage id={desctructableButtonMessageId} />
            </DestructableButton>
          )}
          {footer && <div>{footer}</div>}
        </div>
        {showCancel && (
          <SecondaryButton onClick={onClose} disabled={loading} type="button">
            <FormattedMessage id={secondaryButtonMessageId} />
          </SecondaryButton>
        )}
        {showSave && (
          <PrimaryButton onClick={onSubmit} disabled={disabled || loading || !canSubmit} type="submit">
            <FormattedMessage id={primaryButtonMessageId} />
          </PrimaryButton>
        )}
        {showSaveAsDestructable && (
          <DestructableButton onClick={onSubmit} disabled={disabled || loading || !canSubmit}>
            <FormattedMessage id={primaryButtonMessageId} />
          </DestructableButton>
        )}
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      {...(clickOutsideToClose ? {} : { backdrop: "static" })}
      onHide={onClose}
      size={size}
      centered={centered}
      {...props}
    >
      {aside ? (
        <div css={styles.withAside}>
          {content}
          <aside css={styles.aside}>
            <div css={styles.asideContent}>{aside}</div>
          </aside>
        </div>
      ) : (
        content
      )}
    </Modal>
  );
};

const styles = {
  footer_container: css`
    margin-right: auto;
    display: flex;
    gap: 1rem;
    color: ${colors.grayAnatomyLight1};
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: ${fontSize.medium};
    font-weight: 500;
  `,
  info: css`
    font-size: ${fontSize.normal};
    text-align: right;
    font-weight: 400;
  `,
  subTitle: css`
    font-size: ${fontSize.normal};
    font-weight: 400;
    margin-top: ${margins.xxsmall};
  `,
  withAside: css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 32rem;
    grid-template-rows: auto 1fr auto;

    > .modal-header {
      grid-column: 1 / -1;
    }

    > .modal-body {
      grid-column: 1;
      min-height: min(48rem, 60vh);
    }

    > .modal-footer {
      grid-column: 1;
    }

    @media (max-width: ${breakpoints.mobile}) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  aside: css`
    grid-column: 2;
    grid-row: 2 / span 2;
    position: relative;
    background: ${colors.grayAnatomyLight5};
    border-left: 1px solid ${colors.grayAnatomyLight3};

    @media (max-width: ${breakpoints.mobile}) {
      grid-column: 1;
      grid-row: auto;
      border-left: 0;
      border-top: 1px solid ${colors.grayAnatomyLight3};
    }
  `,
  asideContent: css`
    position: absolute;
    inset: 0;
    overflow-y: auto;
    padding: 2.4rem;

    @media (max-width: ${breakpoints.mobile}) {
      position: static;
      max-height: 40vh;
    }
  `,
};

ModalWindow.defaultProps = {
  showSave: true,
  showSaveAsDestructable: false,
  showCancel: true,
  primaryButtonMessageId: `Global.Save`,
  secondaryButtonMessageId: `Global.Cancel`,
  desctructableButtonMessageId: `Global.Delete`,
  disabled: false,
  clickOutsideToClose: false,
  centered: true,
};

ModalWindow.propTypes = {
  title: PropTypes.element,
  subTitle: PropTypes.string,
  info: PropTypes.element,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onDestructive: PropTypes.func,
  showSave: PropTypes.bool,
  showSaveAsDestructable: PropTypes.bool,
  showCancel: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  children: PropTypes.any.isRequired,
  isDestructable: PropTypes.bool,
  canSubmit: PropTypes.bool,
  primaryButtonMessageId: PropTypes.string,
  secondaryButtonMessageId: PropTypes.string,
  desctructableButtonMessageId: PropTypes.string,
  footer: PropTypes.any,
  clickOutsideToClose: PropTypes.bool,
  centered: PropTypes.bool,
  aside: PropTypes.node,
};

export default ModalWindow;
